import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiClipboard, FiFileText, FiClock, FiCheckCircle, FiChevronRight, FiUploadCloud, FiAlertCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StatCard from '../../components/ui/StatCard';
import SectionCard from '../../components/ui/SectionCard';
import ListItem from '../../components/ui/ListItem';
import EmptyState from '../../components/ui/EmptyState';
import StatusBadge from '../../components/ui/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { getDataEntryStats } from '../../services/statsService';
import { getQueuedCount, syncQueuedCaptures, MAX_QUEUED_CAPTURES } from '../../services/captureService';
import { timeAgo } from '../../utils/format';

export default function DataEntryDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    let active = true;
    getDataEntryStats(user.id).then((data) => {
      if (active) setStats(data);
    });
    return () => {
      active = false;
    };
  }, [user.id]);

  useEffect(() => {
    setPendingCount(getQueuedCount());
  }, []);

  async function handleSync() {
    setSyncing(true);
    setSyncError(null);
    try {
      await syncQueuedCaptures();
      setPendingCount(getQueuedCount());
    } catch (err) {
      setSyncError(err.message || 'No se pudo sincronizar. Intenta de nuevo.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <DashboardLayout
      title={`Hola, ${user.name.split(' ')[0]}`}
      subtitle="Operativo · Vista general"
    >
      {pendingCount > 0 && (
        <SectionCard>
          <div className="sync-banner">
            <span className="sync-banner__icon"><FiUploadCloud /></span>
            <span className="sync-banner__text">
              <span className="sync-banner__title">
                {pendingCount}/{MAX_QUEUED_CAPTURES} encuestas sin sincronizar
              </span>
              <span className="sync-banner__subtitle">Guardadas en este dispositivo</span>
            </span>
            <button type="button" className="btn btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Sincronizando…' : 'Sincronizar'}
            </button>
          </div>
          {syncError && (
            <div className="form-error" role="alert" style={{ marginTop: 10 }}>
              <FiAlertCircle /> {syncError}
            </div>
          )}
        </SectionCard>
      )}

      <section className="kpi-grid">
        <StatCard icon={<FiClipboard />} value={stats?.assignedForms ?? '—'} label="Formularios activos" tone="muted" />
        <StatCard icon={<FiFileText />} value={stats?.totalSubmissions ?? '—'} label="Mis registros" />
        <StatCard icon={<FiClock />} value={stats?.pending ?? '—'} label="Pendientes" tone="warning" />
        <StatCard icon={<FiCheckCircle />} value={stats?.approved ?? '—'} label="Aprobados" tone="success" />
      </section>

      <SectionCard title="Acciones rápidas">
        <ListItem
          as={Link}
          to="/data-entry/forms"
          icon={<FiClipboard />}
          title="Llenar un formulario"
          meta="Elige un formulario asignado"
          right={<FiChevronRight />}
        />
        <ListItem
          as={Link}
          to="/data-entry/records"
          icon={<FiFileText />}
          title="Ver mis registros"
          meta="Historial y estados"
          right={<FiChevronRight />}
        />
      </SectionCard>

      <SectionCard title="Actividad reciente" action={<Link to="/data-entry/records">Ver todo</Link>}>
        {stats === null && <EmptyState icon={<FiFileText />} title="Cargando actividad…" />}
        {stats?.recent.length === 0 && (
          <EmptyState
            icon={<FiFileText />}
            title="Aún no tienes registros"
            description="Cuando envíes un formulario aparecerá aquí."
          />
        )}
        {stats?.recent.map((item) => (
          <ListItem
            key={item.id}
            icon={<FiFileText />}
            title={item.formTitle}
            meta={timeAgo(item.submittedAt)}
            right={<StatusBadge status={item.status} />}
          />
        ))}
      </SectionCard>
    </DashboardLayout>
  );
}
